import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { categoryKeys, type CategoryListQuery } from '@/services/queryKeys';
import { categoryService } from '@/services/category.service';
import type { CreateCategoryInput, UpdateCategoryInput } from '@tcr-erp/shared';
import { useMemo } from 'react';
import {
  buildCategoryNameMap,
  flattenCategoryTree,
  toCategoryTreeSelectData,
} from '@/utils/categoryTree';

export function useCategoryList(
  query: CategoryListQuery = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: categoryKeys.list(query),
    queryFn: () => categoryService.list(query),
    placeholderData: (prev) => prev,
    retry: 0,
    // Tuỳ chọn `enabled` — mặc định `undefined` (TanStack Query coi như `true`) nên mọi
    // lời gọi cũ không truyền `options` giữ nguyên hành vi (luôn fetch).
    enabled: options.enabled,
  });
}

export function useCategoryTree() {
  return useQuery({
    queryKey: categoryKeys.tree(),
    queryFn: () => categoryService.tree(),
    retry: 0,
  });
}

/**
 * TCR-2483 — toàn bộ danh mục cho ô chọn/lọc. `GET /categories` phân trang (mặc định 50) nên
 * `useCategoryList({})` cắt mất danh mục từ mục 51; dùng cây (`/categories/tree`, không phân trang).
 */
export function useCategoryOptions(options: { enabled?: boolean } = {}) {
  const query = useQuery({
    queryKey: categoryKeys.tree(),
    queryFn: () => categoryService.tree(),
    retry: 0,
    enabled: options.enabled,
  });
  const tree = query.data;
  const derived = useMemo(() => {
    const nodes = tree ?? [];
    return {
      treeData: toCategoryTreeSelectData(nodes),
      options: flattenCategoryTree(nodes),
      nameById: buildCategoryNameMap(nodes),
    };
  }, [tree]);
  return { ...derived, isLoading: query.isLoading };
}

export function useCategoryDetail(id: string | undefined) {
  return useQuery({
    queryKey: categoryKeys.detail(id ?? '__none__'),
    queryFn: () => categoryService.getById(id!),
    enabled: !!id,
    retry: 0,
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCategoryInput) => categoryService.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: categoryKeys.all }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCategoryInput }) =>
      categoryService.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: categoryKeys.all }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => categoryService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: categoryKeys.all }),
  });
}

export function useReorderCategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orders: { id: string; ordering: number }[]) =>
      categoryService.reorder(orders),
    onSuccess: () => qc.invalidateQueries({ queryKey: categoryKeys.all }),
  });
}
